import { APIComponentAttributes } from '@ardoq/api-types';
import { differenceInDays } from '@ardoq/date-time';
import { parseLastUpdated } from './freshness';

export type CompletenessBand = 'low' | 'medium' | 'high';

export type CompletenessScore = {
  score: number;
  band: CompletenessBand;
};

export type FreshnessGap =
  | { kind: 'stale'; days: number }
  | { kind: 'unknown' };

export type OwnershipGap =
  | { kind: 'missing' }
  | { kind: 'partial'; state: string };

export type CompletenessGaps = {
  ownershipGap: OwnershipGap | null;
  businessValueMissingFields: BusinessValueFieldKey[];
  techFitMissingFields: TechFitFieldKey[];
  freshnessGap: FreshnessGap | null;
};

const OWNERSHIP_WEIGHT = 40;
const BUSINESS_VALUE_WEIGHT = 20;
const TECH_FIT_WEIGHT = 20;
const FRESHNESS_WEIGHT = 20;

const OWNERSHIP_STATE_FIELD = 'application_ownership_state';

const OWNERSHIP_FACTOR_BY_STATE: Record<string, number> = {
  Managed: 1.0,
  Owned: 0.66,
  Nominated: 0.33,
};

const BUSINESS_VALUE_WEIGHTS = {
  functional_fit: 0.2,
  features_and_usability: 0.2,
  business_strategic_fit: 0.25,
  criticality: 0.35,
} as const satisfies Record<string, number>;

const TECH_FIT_WEIGHTS = {
  technical_integrity: 0.43,
  maintainability_and_agility: 0.27,
  technical_strategic_fit: 0.3,
} as const satisfies Record<string, number>;

type BusinessValueFieldKey = keyof typeof BUSINESS_VALUE_WEIGHTS;
type TechFitFieldKey = keyof typeof TECH_FIT_WEIGHTS;
export type CompletenessFieldKey = BusinessValueFieldKey | TechFitFieldKey;

export const FRESH_THRESHOLD_IN_DAYS = 183;

const FRESHNESS_TIERS: [thresholdInDays: number, factor: number][] = [
  [FRESH_THRESHOLD_IN_DAYS, 1.0],
  [365, 0.66],
  [730, 0.33],
];

const MEDIUM_BAND_MIN = 45;
const HIGH_BAND_MIN = 85;
const MAX_COMPLETENESS_SCORE = 100;

export const isCompleteScore = (score: number): boolean =>
  score === MAX_COMPLETENESS_SCORE;

const readField = (component: APIComponentAttributes, key: string): unknown =>
  (component as Record<string, unknown>)[key];

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

const getFieldPresenceScore = (
  component: APIComponentAttributes,
  weights: Record<string, number>
): number =>
  Object.entries(weights).reduce(
    (total, [key, weight]) =>
      total + (hasValue(readField(component, key)) ? weight : 0),
    0
  );

const getMissingFields = <TKey extends string>(
  component: APIComponentAttributes,
  weights: Record<TKey, number>
): TKey[] =>
  (Object.keys(weights) as TKey[]).filter(
    key => !hasValue(readField(component, key))
  );

const getFreshnessGap = (
  component: APIComponentAttributes,
  now: Date
): FreshnessGap | null => {
  const updated = parseLastUpdated(component);
  if (!updated) return { kind: 'unknown' };
  const days = Math.max(0, differenceInDays(now, updated));
  if (days < FRESH_THRESHOLD_IN_DAYS) return null;
  return { kind: 'stale', days };
};

const getOwnershipGap = (
  component: APIComponentAttributes
): OwnershipGap | null => {
  if (getOwnershipFactor(component) >= 1) return null;
  const state = readField(component, OWNERSHIP_STATE_FIELD);
  if (typeof state !== 'string' || state === '') return { kind: 'missing' };
  return { kind: 'partial', state };
};

export const getCompletenessGaps = (
  component: APIComponentAttributes,
  now: Date
): CompletenessGaps => ({
  ownershipGap: getOwnershipGap(component),
  businessValueMissingFields: getMissingFields(
    component,
    BUSINESS_VALUE_WEIGHTS
  ),
  techFitMissingFields: getMissingFields(component, TECH_FIT_WEIGHTS),
  freshnessGap: getFreshnessGap(component, now),
});

const getOwnershipFactor = (component: APIComponentAttributes): number => {
  const state = readField(component, OWNERSHIP_STATE_FIELD);
  if (typeof state !== 'string') return 0;
  return OWNERSHIP_FACTOR_BY_STATE[state] ?? 0;
};

const getFreshnessFactor = (
  component: APIComponentAttributes,
  now: Date
): number => {
  const updated = parseLastUpdated(component);
  if (!updated) return 0;

  const daysSinceUpdate = Math.max(0, differenceInDays(now, updated));
  const tier = FRESHNESS_TIERS.find(
    ([thresholdInDays]) => daysSinceUpdate < thresholdInDays
  );
  return tier ? tier[1] : 0;
};

export const toBand = (score: number): CompletenessBand => {
  if (score < MEDIUM_BAND_MIN) return 'low';
  if (score < HIGH_BAND_MIN) return 'medium';
  return 'high';
};

export const getCompletenessScore = (
  component: APIComponentAttributes,
  now: Date
): CompletenessScore => {
  const ownershipPoints = Math.round(
    OWNERSHIP_WEIGHT * getOwnershipFactor(component)
  );
  const businessValuePoints = Math.round(
    BUSINESS_VALUE_WEIGHT *
      getFieldPresenceScore(component, BUSINESS_VALUE_WEIGHTS)
  );
  const techFitPoints = Math.round(
    TECH_FIT_WEIGHT * getFieldPresenceScore(component, TECH_FIT_WEIGHTS)
  );
  const freshnessPoints = Math.round(
    FRESHNESS_WEIGHT * getFreshnessFactor(component, now)
  );

  const score =
    ownershipPoints + businessValuePoints + techFitPoints + freshnessPoints;

  return { score, band: toBand(score) };
};
